import classNames from "classnames";
import {
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
  forwardRef,
  useId,
} from "react";
import styles from "./css/TextField.module.css";

interface BaseProps {
  label?: string;
  // 선택 입력 항목일 때 라벨 옆에 표시할 문구 (예: "선택")
  optionalText?: string;
  helperText?: string;
  errorText?: string;
  maxLength?: number;
  showCount?: boolean;
  trailing?: ReactNode;
}

type InputProps = BaseProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, "maxLength"> & {
    multiline?: false;
  };

type TextareaProps = BaseProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "maxLength"> & {
    multiline: true;
  };

export type TextFieldProps = InputProps | TextareaProps;

const TextField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextFieldProps
>(function TextField(props, ref) {
  const {
    label,
    optionalText,
    helperText,
    errorText,
    maxLength,
    showCount = false,
    trailing,
    className,
    id,
    multiline,
    ...rest
  } = props;
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;
  const message = errorText ?? helperText;
  const length = String(rest.value ?? "").length;

  const controlProps = {
    id: inputId,
    maxLength,
    "aria-invalid": errorText ? true : undefined,
    "aria-describedby": message ? messageId : undefined,
    className: styles.control,
  };

  return (
    <div className={classNames(styles.field, className)}>
      {label && (
        <label className={styles.label} htmlFor={inputId}>
          {label}
          {optionalText && (
            <span className={styles.optional}>{optionalText}</span>
          )}
        </label>
      )}
      <div
        className={classNames(styles.box, {
          [styles.error]: errorText,
          [styles.multiline]: multiline,
        })}
      >
        {multiline ? (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            {...controlProps}
            {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        ) : (
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            {...controlProps}
            {...(rest as InputHTMLAttributes<HTMLInputElement>)}
          />
        )}
        {trailing && <span className={styles.trailing}>{trailing}</span>}
      </div>
      {(message || (showCount && maxLength)) && (
        <div className={styles.footer}>
          <span
            id={messageId}
            className={classNames(styles.message, {
              [styles.errorMessage]: errorText,
            })}
          >
            {message}
          </span>
          {showCount && maxLength && (
            <span className={styles.count}>
              {length.toLocaleString()}/{maxLength.toLocaleString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
});

export default TextField;
